import { useCallback, useEffect, useState } from "react";

import {
  Download,
  Image,
  CheckCircle,
  Clock,
  AlertCircle,
  Maximize2,
  X,
  Save,
} from "lucide-react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { MEDIA_COLLECTIONS } from "../../../utils/defaultSettings";
import { SaveAs } from "@mui/icons-material";
import { URLS } from "../../../constants";
import { uvenuFetcher } from "../../../utils/uvenu-fetcher";
import UvDialog from "../../../components/UI/UvDialog";
const ConversionResults = ({
  onSave,
  results,
  isProcessing,
  isSaving,
  eventId,
  loggedInUserObj,
}) => {
  // Dummy results for testing
  // Uncomment the following lines to use dummy data for testing purposes
  // results = [
  //   {
  //     id: "6862b9d45692fe040d33e53f",
  //     api: { name: "AI Generated: Pixverse Effects" },
  //     status: "completed",
  //     createdAt: 1751300564,
  //     isBulkUploaded: false,
  //     isIosTypeSupported: false,
  //     isRecorded: false,
  //     isStarred: false,
  //     likeCount: 0,
  //     convertedUrl:
  //       "https://dev-cdn.uvenu.com/events/65fabb64596fc8004df55e78/sharedMedia/mediaFile-6862b9d45692fe040d33e53f.mp4",
  //     metadata: {
  //       width: 0,
  //       height: 0,
  //       orientation: "",
  //     },
  //     mimeType: "video/mp4",
  //     showOnQuotesFeed: false,
  //     userId: "",
  //     userLocation: {
  //       type: "Point",
  //       coordinates: [0, 0],
  //       name: "",
  //     },
  //     videoPosterUrl: null,
  //   },
  // ];
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxContent, setLightboxContent] = useState(null);
  const [selectedCollection, setSelectedCollection] = useState(5); // Default to "Production Media"
  const [showSaveButton, setShowSaveButton] = useState(true);
  const [promptName, setPromptName] = useState("");
  const [showSavePromptDialog, setShowSavePromptDialog] = useState(false);
  const [result, setResult] = useState({});
  useEffect(() => {
    // Reset selected collection when results change
    if (results.length > 0 && results[0].convertedUrl?.includes(".mp4")) {
      setShowSaveButton(false);
    } else {
      setShowSaveButton(true);
    }
    if (results.length > 0) {
      setResult(results[0]);
    } else {
      setResult({});
    }
  }, [results]);
  const handleDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
    }
  };

  const openLightbox = (url, type, apiName) => {
    setLightboxContent({ url, type, name: apiName });
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxContent(null);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case "processing":
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case "error":
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
        return (
          <Badge className="bg-green-100 text-green-700 border-green-200">
            Completed
          </Badge>
        );
      case "processing":
        return (
          <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
            Processing
          </Badge>
        );
      case "error":
        return (
          <Badge className="bg-red-100 text-red-700 border-red-200">
            Error
          </Badge>
        );
      default:
        return null;
    }
  };
  const handleSave = () => {
    const {
      convertedUrl,
      api: { name },
    } = results[0];
    onSave(selectedCollection, convertedUrl, name);
  };

  const handleSavePrompt = useCallback(async () => {
    const inputs = result?.inputs || {};
    const prompt = inputs.prompt || "";
    const payload = {
      eventId,
      prompt,
      name: promptName,
      exampleImageUrl: result?.convertedUrl,
      userId: loggedInUserObj.email,
      apiId: result?.api?.id,
      sourceImageUrls: (result?.inputs?.image_urls && Array.isArray(result.inputs.image_urls)) ? result.inputs.image_urls : [],
    };
    if (result?.api?.id !== "pixverse-transition") {
      payload["seed"] = result?.seed;
    }
    try {
      const response = await uvenuFetcher({
        method: "POST",
        url: URLS.SAVE_PROMPT,
        body: payload,
        headers: {
          token: `${loggedInUserObj.token}`,
        },
      });
      if (response.statusCode === 200 || response.statusCode === 201) {
        console.log("Prompt saved successfully");
      } else {
        console.log(response.json.message);
        return "error: " + response.json.message;
      }
    } catch (error) {
      console.log(error);
    } finally {
      setPromptName("");
      setShowSavePromptDialog(false);
    }
  }, [result, promptName, eventId, loggedInUserObj]);

  if (!isProcessing && results.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="w-12 h-12 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3">
          <Image className="w-6 h-6 text-gray-400" />
        </div>
        <h3 className="text-sm font-medium text-gray-300 mb-1">
          No results yet
        </h3>
        <p className="text-xs text-gray-500">
          Upload files and select an AI service
        </p>
      </div>
    );
  }

  if (isProcessing && results.length === 0) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mx-auto mb-3"></div>
        <h3 className="text-sm font-medium text-gray-300 mb-1">
          Processing...
        </h3>
        <p className="text-xs text-gray-500">Converting your files with AI</p>
      </div>
    );
  }

  return (
    <>
      {/* Fixed height scrollable container */}
      <div className="max-h-full overflow-y-auto flex flex-col w-full">
        <div className="flex-1">
          <div className="space-y-4 pr-3">
            {results.map((result) => (
              <div
                key={result.id}
                className="bg-gray-700/50 rounded-lg p-4 border border-gray-600"
              >
                {/* Header with status only */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    {getStatusIcon(result.status)}
                    <span className="font-medium text-gray-200 text-sm">
                      {result.api.name} Result
                    </span>
                  </div>
                  {getStatusBadge(result.status)}
                </div>

                {/* Error message */}
                {result.status === "error" && result.error && (
                  <p className="text-sm text-red-400 mb-3 p-2 bg-red-900/20 rounded">
                    {result.error}
                  </p>
                )}

                {/* Processing indicator */}
                {result.status === "processing" && (
                  <div className="flex items-center justify-center space-x-2 py-6">
                    <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-gray-400 text-sm">Converting...</span>
                  </div>
                )}

                {/* Media Preview - Fixed height container that never expands */}
                {result.status === "completed" && result.convertedUrl && (
                  <div className="space-y-3">
                    <div
                      className="relative  overflow-y-auto w-full rounded bg-gray-800 border border-gray-600 relative group cursor-pointer flex items-center justify-center"
                      onClick={() =>
                        openLightbox(
                          result.convertedUrl,
                          result.api.name.toLowerCase().includes("video") ||
                            result.convertedUrl.includes(".mp4")
                            ? "video"
                            : "image",
                          result.api.name
                        )
                      }
                    >
                      {result.api.name.toLowerCase().includes("video") ||
                      result.convertedUrl.includes(".mp4") ? (
                        <video
                          src={result.convertedUrl}
                          className="max-w-full max-h-[300px] object-contain"
                          muted
                          loop
                          controls
                        />
                      ) : (
                        <img
                          src={result.convertedUrl}
                          alt="Converted result"
                          className="max-w-full max-h-[300px] object-contain"
                        />
                      )}

                      {/* Expand overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-200 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gray-900/80 rounded-full p-2">
                          <Maximize2 className="w-5 h-5 text-white" />
                        </div>
                      </div>
                    </div>
                    <div className="w-full flex justify-between items-center">
                      {/* Download button under media */}
                      <Button
                        size="sm"
                        onClick={() =>
                          handleDownload(
                            result.convertedUrl,
                            `converted-${result.api.name
                              .toLowerCase()
                              .replace(/\s+/g, "-")}`
                          )
                        }
                        className="!text-white"
                        variant="contained"
                      >
                        <Download color="white" className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => {
                          setShowSavePromptDialog(true);
                        }}
                        className="!text-white"
                        variant="contained"
                      >
                        <SaveAs color="white" className="w-4 h-4 mr-2" />
                        Save Prompt
                      </Button>
                      {showSaveButton && (
                        <>
                          <div>
                            <Typography
                              htmlFor="destination-select"
                              className="text-sm font-small text-gray-200 !mb-1 !mt-2"
                            >
                              Select Save Destination
                            </Typography>
                            <select
                              id="destination-select"
                              name="destination"
                              value={selectedCollection}
                              onChange={(e) =>
                                setSelectedCollection(Number(e.target.value))
                              }
                              className="cursor-pointer w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
                            >
                              {MEDIA_COLLECTIONS.map((collection) => (
                                <option
                                  key={collection.id}
                                  value={collection.id}
                                  className="cursor-pointer"
                                >
                                  {collection.name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <Button
                            size="sm"
                            variant="contained"
                            onClick={handleSave}
                            className="!text-white"
                          >
                            {isSaving ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            ) : (
                              <Save color="white" className="w-4 h-4 mr-2" />
                            )}
                            {isSaving ? "Saving..." : "Save"}
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox Dialog */}
      <Dialog
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
        className="ai-result-lightbox"
      >
        <DialogContent className="max-w-4xl w-[80vw] h-[80vh] p-4 bg-black border-gray-700 overflow-hidden flex flex-col">
          <DialogTitle className="text-white text-center">
            {lightboxContent?.name} Result
          </DialogTitle>

          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-50 bg-gray-900/80 hover:bg-gray-800 text-white rounded-full p-2 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Media content - properly contained */}
          <div className="flex-1 flex items-center justify-center min-h-0 p-4">
            {lightboxContent &&
              (lightboxContent.type === "video" ? (
                <video
                  src={lightboxContent.url}
                  className="max-w-full max-h-full object-contain"
                  controls
                  autoPlay
                  muted
                  loop
                />
              ) : (
                <img
                  src={lightboxContent.url}
                  alt="Full size result"
                  className="max-w-full max-h-full object-contain"
                />
              ))}
          </div>
        </DialogContent>
      </Dialog>

      <UvDialog
        show={showSavePromptDialog}
        onClose={() => {
          setPromptName("");
          setShowSavePromptDialog(false);
        }}
        onSubmit={handleSavePrompt}
        title="Save Prompt"
        submitButtonLable="Save"
      >
        <TextField
          label="Prompt Name"
          variant="outlined"
          fullWidth
          value={promptName}
          onChange={(e) => {
            setPromptName(e.target.value);
          }}
          margin="normal"
          sx={{ marginBottom: "20px" }}
        />
      </UvDialog>
    </>
  );
};
export default ConversionResults;
